import { useState, useEffect, useCallback } from 'react';
import { enqueteService, Enquete, EnqueteFormData } from '../services/enqueteService';
import { ApiError } from '../services/api';

interface UseEnquetesOptions {
  page?: number;
  limit?: number;
  search?: string;
  statut?: string;
  priorite?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface UseEnquetesReturn {
  // Données
  enquetes: Enquete[];
  total: number;
  loading: boolean;
  error: string | null;
  
  // Actions
  createEnquete: (data: EnqueteFormData) => Promise<void>;
  updateEnquete: (id: string, data: Partial<EnqueteFormData>) => Promise<void>;
  deleteEnquete: (id: string) => Promise<void>;
  refreshEnquetes: () => Promise<void>;
  
  // Pagination et filtres
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setStatut: (statut: string) => void;
  setPriorite: (priorite: string) => void;
  setSortBy: (sortBy: string) => void;
  setSortOrder: (sortOrder: 'asc' | 'desc') => void;
  
  // Statistiques
  stats: {
    total: number;
    enCours: number;
    terminees: number;
    enAttente: number;
    hautePriorite: number;
  } | null;
  statsLoading: boolean;
  statsError: string | null;
}

export const useEnquetes = (options: UseEnquetesOptions = {}): UseEnquetesReturn => {
  // État local
  const [enquetes, setEnquetes] = useState<Enquete[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // État des filtres
  const [page, setPage] = useState(options.page || 1);
  const [search, setSearch] = useState(options.search || '');
  const [statut, setStatut] = useState(options.statut || 'tous');
  const [priorite, setPriorite] = useState(options.priorite || 'tous');
  const [sortBy, setSortBy] = useState(options.sortBy || 'date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(options.sortOrder || 'desc');
  
  // État des statistiques
  const [stats, setStats] = useState<{
    total: number;
    enCours: number;
    terminees: number;
    enAttente: number;
    hautePriorite: number;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Fonction pour charger les enquêtes
  const loadEnquetes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Loading enquêtes with auth token:', localStorage.getItem('accessToken') ? 'Present' : 'Missing');
      
      // Mapper les paramètres de tri frontend vers backend
      const mapSortBy = (frontendSortBy: string) => {
        switch (frontendSortBy) {
          case 'date': return 'dateModification';
          case 'priorite': return 'priorite';
          case 'statut': return 'statut';
          case 'titre': return 'titre';
          default: return 'dateModification';
        }
      };

      const response = await enqueteService.getEnquetes({
        page,
        limit: 10,
        search,
        statut: statut === 'tous' ? undefined : statut,
        priorite: priorite === 'tous' ? undefined : priorite,
        sortBy: mapSortBy(sortBy),
        sortOrder
      });
      
      console.log('📊 Enquêtes loaded:', response.data.enquetes.length, 'enquêtes');
      setEnquetes(response.data.enquetes);
      setTotal(response.data.total);
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors du chargement des enquêtes';
      setError(errorMessage);
      console.error('Erreur lors du chargement des enquêtes:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statut, priorite, sortBy, sortOrder]);

  // Fonction pour charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      
      const response = await enqueteService.getEnquetesStats();
      setStats(response.data);
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors du chargement des statistiques';
      setStatsError(errorMessage);
      console.error('Erreur lors du chargement des statistiques:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    loadEnquetes();
  }, [loadEnquetes]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Fonction pour créer une enquête
  const createEnquete = useCallback(async (data: EnqueteFormData) => {
    try {
      setLoading(true);
      console.log('💾 Creating enquête:', data.titre);
      console.log('🔍 Auth token present:', localStorage.getItem('accessToken') ? 'Yes' : 'No');
      
      const result = await enqueteService.createEnquete(data);
      console.log('✅ Enquête created successfully:', result);
      
      await loadEnquetes(); // Recharger la liste
      await loadStats(); // Recharger les stats
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors de la création de l\'enquête';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadEnquetes, loadStats]);

  // Fonction pour mettre à jour une enquête
  const updateEnquete = useCallback(async (id: string, data: Partial<EnqueteFormData>) => {
    try {
      setLoading(true);
      await enqueteService.updateEnquete(id, data);
      await loadEnquetes(); // Recharger la liste
      await loadStats(); // Recharger les stats
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors de la mise à jour de l\'enquête';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadEnquetes, loadStats]);

  // Fonction pour supprimer une enquête
  const deleteEnquete = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await enqueteService.deleteEnquete(id);
      await loadEnquetes(); // Recharger la liste
      await loadStats(); // Recharger les stats
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors de la suppression de l\'enquête';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadEnquetes, loadStats]);

  // Fonction pour rafraîchir les données
  const refreshEnquetes = useCallback(async () => {
    await loadEnquetes();
    await loadStats();
  }, [loadEnquetes, loadStats]);

  return {
    // Données
    enquetes,
    total,
    loading,
    error,
    
    // Actions
    createEnquete,
    updateEnquete,
    deleteEnquete,
    refreshEnquetes,
    
    // Pagination et filtres
    setPage,
    setSearch,
    setStatut,
    setPriorite,
    setSortBy,
    setSortOrder,
    
    // Statistiques
    stats,
    statsLoading,
    statsError
  };
}; 